import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { StatusBadge } from '@/components/hr/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

// ── Types ─────────────────────────────────────────────────────────────────────

type Employee = {
  id: number
  firstName: string
  lastName: string
}

type Enrollment = {
  id: number
  eventId: number
  employeeId: number
  employeeFirstName: string | null
  employeeLastName: string | null
  status: string
  feedback: string | null
  rating: number | null
}

type TrainingEventDetail = {
  id: number
  name: string
  type: string
  status: string
  description: string | null
  trainerName: string | null
  startDate: string | Date
  endDate: string | Date | null
  startTime: string | null
  endTime: string | null
  location: string | null
  maxParticipants: number | null
  enrollments: Enrollment[]
  enrollmentCount: number
}

// ── Constants ─────────────────────────────────────────────────────────────────

const trainingTypeColors: Record<string, string> = {
  internal: 'bg-blue-100 text-blue-800',
  external: 'bg-purple-100 text-purple-800',
  online: 'bg-cyan-100 text-cyan-800',
  workshop: 'bg-orange-100 text-orange-800',
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/hr/training/$eventId')({
  component: TrainingEventDetailPage,
})

function TrainingEventDetailPage() {
  const params = Route.useParams()
  const eventId = Number(params.eventId)
  const queryClient = useQueryClient()

  const { data: eventRaw, isLoading } = useQuery(
    orpc.hr.getTrainingEvent.queryOptions({ input: { id: eventId } }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const event = eventRaw as TrainingEventDetail | null
  const employees = (employeesData?.data ?? []) as Employee[]

  const [isEnrollOpen, setIsEnrollOpen] = useState(false)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: [['hr', 'getTrainingEvent']] })
  }

  const updateEventMutation = useMutation(
    orpc.hr.updateTrainingEvent.mutationOptions({
      onSuccess: () => {
        toast.success('Event updated')
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const enrollMutation = useMutation(
    orpc.hr.enrollEmployee.mutationOptions({
      onSuccess: () => {
        toast.success('Employee enrolled')
        refetch()
        setIsEnrollOpen(false)
        setSelectedEmployeeId('')
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateEnrollmentMutation = useMutation(
    orpc.hr.updateEnrollment.mutationOptions({
      onSuccess: () => {
        toast.success('Enrollment updated')
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const removeEnrollmentMutation = useMutation(
    orpc.hr.removeEnrollment.mutationOptions({
      onSuccess: () => {
        toast.success('Enrollment removed')
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const handleEnroll = () => {
    if (!selectedEmployeeId) return
    enrollMutation.mutate({ eventId, employeeId: Number(selectedEmployeeId) })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-48 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-muted-foreground">Training event not found</p>
      </div>
    )
  }

  // Build enrolled employee IDs to exclude from picker
  const enrolledIds = new Set(event.enrollments.map((e) => e.employeeId))
  const availableEmployees = employees.filter((emp) => !enrolledIds.has(emp.id))

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-4 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Header card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl">{event.name}</CardTitle>
              <CardDescription className="mt-2 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    className={`${trainingTypeColors[event.type] ?? ''} capitalize`}
                    variant="outline"
                  >
                    {event.type}
                  </Badge>
                  {event.trainerName && (
                    <span className="text-sm">Trainer: {event.trainerName}</span>
                  )}
                  {event.location && (
                    <span className="text-sm text-muted-foreground">{event.location}</span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {new Date(event.startDate).toLocaleDateString()}
                  {event.endDate && ` – ${new Date(event.endDate).toLocaleDateString()}`}
                  {event.startTime && ` · ${event.startTime}`}
                  {event.endTime && ` – ${event.endTime}`}
                </div>
                {event.maxParticipants && (
                  <div className="text-sm text-muted-foreground">
                    {event.enrollmentCount} / {event.maxParticipants} enrolled
                  </div>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <StatusBadge status={event.status} />
              {event.status === 'planned' && (
                <Button
                  size="sm"
                  onClick={() =>
                    updateEventMutation.mutate({ id: event.id, status: 'in_progress' })
                  }
                  disabled={updateEventMutation.isPending}
                >
                  Start
                </Button>
              )}
              {event.status === 'in_progress' && (
                <Button
                  size="sm"
                  onClick={() =>
                    updateEventMutation.mutate({ id: event.id, status: 'completed' })
                  }
                  disabled={updateEventMutation.isPending}
                >
                  Complete
                </Button>
              )}
              {(event.status === 'planned' || event.status === 'in_progress') && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600"
                  onClick={() =>
                    updateEventMutation.mutate({ id: event.id, status: 'cancelled' })
                  }
                  disabled={updateEventMutation.isPending}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {event.description && (
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {event.description}
            </p>
          </CardContent>
        )}
      </Card>

      {/* Enrollments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Enrollments ({event.enrollmentCount})</CardTitle>
            <Button size="sm" onClick={() => setIsEnrollOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Enroll Employee
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {event.enrollments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No enrollments yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Feedback</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {event.enrollments.map((enrollment) => (
                  <TableRow key={enrollment.id}>
                    <TableCell className="font-medium">
                      {enrollment.employeeFirstName
                        ? `${enrollment.employeeFirstName} ${enrollment.employeeLastName}`
                        : `Employee #${enrollment.employeeId}`}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={enrollment.status}
                        onValueChange={(v) =>
                          updateEnrollmentMutation.mutate({
                            id: enrollment.id,
                            status: v as 'enrolled' | 'attended' | 'absent' | 'cancelled',
                          })
                        }
                      >
                        <SelectTrigger className="w-[130px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="enrolled">Enrolled</SelectItem>
                          <SelectItem value="attended">Attended</SelectItem>
                          <SelectItem value="absent">Absent</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {enrollment.rating ? (
                        <span className="text-yellow-500">
                          {'★'.repeat(enrollment.rating)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground text-sm">
                      {enrollment.feedback ?? '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => removeEnrollmentMutation.mutate({ id: enrollment.id })}
                        disabled={removeEnrollmentMutation.isPending}
                        title="Remove enrollment"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enroll Employee Dialog */}
      <Dialog open={isEnrollOpen} onOpenChange={setIsEnrollOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Enroll Employee</DialogTitle>
            <DialogDescription>Select an employee to enroll in this training</DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {availableEmployees.length === 0 ? (
                  <SelectItem value="__none__" disabled>
                    All employees enrolled
                  </SelectItem>
                ) : (
                  availableEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id.toString()}>
                      {emp.firstName} {emp.lastName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              onClick={handleEnroll}
              disabled={!selectedEmployeeId || enrollMutation.isPending}
            >
              {enrollMutation.isPending ? 'Enrolling...' : 'Enroll'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
